import React, {useEffect, useRef, useState} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLoading, usePersistedTransactionId } from '../../context';
import {
  HeroLogo,
  Footer,
  Button,
  Label,
  TextInput,
  FormItemContainer,
  FormContainer, SingleLineFormItemWrapper,
} from '../../components';
import { debounce } from 'radash';
import AliPayPlusPaymentImage from '../../resource/figma-images/alipay-plus-payment-image.png';
import ContinueToPayImage from '../../resource/figma-images/continue-to-pay-image.svg';
import CreditCardPaymentImage from '../../resource/figma-images/credit-card-payment-image.svg';
import { TillPaymentInlineForm } from './TillPaymentInlineForm';
import { Alert, Stack, styled } from '@mui/material';
import ReCAPTCHA from "react-google-recaptcha";
import {
  getDefaultPaymentMethod,
  getDisplaySurchargeRate,
  getPageHeroLogo,
  getThemeColor,
  shouldHideAlipayPlus,
  shouldVerifyRecaptcha
} from "../../customHostConfigOnClient";
import {
  PaymentContent,
  PaymentDetails,
  PaymentHiddenWrapper,
  PaymentOption,
  PaymentOptionImage,
  PaymentOptionList,
  PaymentOptionText,
  SurchargeAmountWrapper
} from "../../components/PaymentMethod";
import {useRecaptchaReady, verifyRecaptcha} from "../../utils/useRecaptchaReady";
import CreditCardPaymentImageTwo from "../../resource/creditCardPaymentImage-two.png";


const InfoMessage = styled('div')`
  color: darkgray;
  text-align: center;
`;

const BillerLongName = styled('div')`
  color: darkgray;
  text-align: left;
  font-weight: bold;
`;

const PaymentFormPageTitle = styled('div')`
  font-size: 15px;
  text-align: center;
  font-weight: 500;
  color: #747F94;
`;

const Agreement = styled(Stack)`
  flex-direction: row;
  font-size: 13px;
  gap: 10px;
  align-items: center;
  justify-content: flex-start;
  padding-bottom: 15px;
`;

const INITIAL_PAYMENT_METHOD = 'initial payment method';

export const BPayPaymentForm = () => {
  const [billAmount, setBillAmount] = useState('');
  const [billReference, setBillReference] = useState('');
  const [billAmountDisabled, setBillAmountDisabled] = useState(false);
  const [billerCode, setBillerCode] = useState('');
  const [billerCodeError, setBillerCodeError] = useState(false);
  const [billerLongName, setBillerLongName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState('');
  const [paymentResult, setPaymentResult] = useState('');
  const [billAmountError, setBillAmountError] = useState(false);
  const [billReferenceError, setBillReferenceError] = useState(false);
  const [wechatExchangeRate, setWechatExchangeRate] = useState(5);
  const tillPaymentInlineFormRef = useRef(null);
  const recaptchaRef = useRef(null);
  const recaptchaReady = useRecaptchaReady();
  const { showLoading, hideLoading } = useLoading();
  const persistedTransactionId = usePersistedTransactionId();

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(INITIAL_PAYMENT_METHOD);

  // Hide /bpay-flow page for non-bpay domains.
  if (window.location.hostname !== 'bpay.oligo.com.au' && window.location.hostname !== 'localhost') {
    window.location = '/' + window.location.search;
  }

  useEffect(() => {
    if (paymentResult !== '') {
      setError('');
      setSelectedPaymentMethod('initial payment method');
    }
  }, [paymentResult]);

  const handlePaymentMethodChange =
    (paymentMethod) => () => {
      setSelectedPaymentMethod(prev => (prev !== paymentMethod ? paymentMethod : prev));
    };

  const envParam = new URLSearchParams(useLocation().search).get('env');

  const handleBillAmountChange = (value) => {
    if (isValidFloat(value)) {
      setBillAmount(value);
      setBillAmountError(false);
    }
  };

  // Store the debounced function in a ref to persist it across renders
  const debouncedHandleBillerCodeChange = useRef(
    debounce(
      {
        delay: 500, // 500ms delay, adjust as needed
      },
      async (value) => {
        setBillerCodeError(false);
        setBillerLongName(''); // Reset long name on each change

        if (value) {
          try {
            const response = await fetch('/api/bpay/validate-biller-code', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ billerCode: value }),
            });
            const data = await response.json();
            if (data.longName) {
              setBillerLongName(data.longName);
              setError(''); // Hide error message
            } else {
              setBillerCodeError(true);
              setError(data.error || 'Biller code validation failed');
            }
          } catch (err) {
            setBillerCodeError(true);
            setError('Biller code validation failed');
          }
        }
      }
    )
  ).current;

  // Handle input change and call the debounced function
  const handleBillerCodeChange = (value) => {
    setBillerCode(value);
    debouncedHandleBillerCodeChange(value);
  };

  const handleBillReferenceChange = (value) => {
    setBillReference(value);
    setBillReferenceError(false);
  };

  const isValidFloat = (value) => {
    return /^-?\d*\.?\d*$/.test(value);
  };

  const calculateCNYAmount = () => {
    const amount = parseFloat(billAmount);
    if (isNaN(amount)) return '';
    return `≈ ￥${(amount * wechatExchangeRate).toFixed(2)}`;
  };

  const createPaymentOrderAndNavigateForAlipay = async (data) => {
    window.location.href = data.redirect_url;
  };

  const getPaymentEndpoint = (selectedPayment) => {
    switch (selectedPayment) {
      case 'alipay':
        return '/api/alipay/create-payment';
      default:
        return '';
    }
  }

  const getPaymentMethodName = (selectPayment) => {
    switch (selectPayment) {
      case 'tillPaymentJs':
        return 'Credit Card';
      case 'alipay':
        return 'Alipay';
      default:
        return '';
    }
  }

  const adjustCreatePaymentEndpoint = (endpoint) => {
    if (window.location.hostname === 'www.vidatennis.com.au') return `https://vida.oligo.com.au${endpoint}`;
    return endpoint;
  }

  const createPaymentOrderAndNavigate = async () => {
    if (!billAmount) {
      setBillAmountError(true);
      return;
    }

    if (!billerCode || billerLongName === '') {
      setBillerCodeError(true);
      return;
    }

    if (!billReference) {
      setBillReferenceError(true);
      return;
    }

    setBillAmountDisabled(true);

    if (selectedPaymentMethod === 'initial payment method') {
      setError('Please select a payment method.');
      return;
    }

    if (shouldVerifyRecaptcha() && recaptchaReady && recaptchaRef.current) {
      // Validate ReCAPTCHA
      await verifyRecaptcha(recaptchaRef, setError);
    }

    try {
      if (selectedPaymentMethod === 'tillPaymentJs') {
        await tillPaymentInlineFormRef.current.paymentSubmit(setPaymentResult);
      } else {
        showLoading();

        const createPaymentEndpoint = getPaymentEndpoint(selectedPaymentMethod);
        const createPaymentEndpointWithAdjustedHost = adjustCreatePaymentEndpoint(createPaymentEndpoint);
        const response = await fetch(`${createPaymentEndpointWithAdjustedHost}?env=${envParam}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            billAmount,
            billReference,
            userEmail,
            persistedTransactionId,
          }),
        });
        const data = await response.json();
        if (data.return_code === 'SUCCESS') {
          if (selectedPaymentMethod === 'alipay' || selectedPaymentMethod === 'alipay-dana') {
            await createPaymentOrderAndNavigateForAlipay(data);
          }
        } else {
          hideLoading('Payment creation failed.', 'error');
        }
      }
    } catch (err) {
      hideLoading();
      setError('Create payment order failed, please try again.');
    }
  };

  const handlePayBill = async () => {
    try {
      await createPaymentOrderAndNavigate();
    } catch (err) {
      setError('Payment validation request sending failed');
      console.error(err);
    }
  };

  return (
    <FormContainer>
      <HeroLogo logo={getPageHeroLogo()} redirectPath='/bpay-flow'/>
      <Stack gap="20px" alignItems="center">
        <PaymentFormPageTitle>Oligo BPAY Portal</PaymentFormPageTitle>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack gap="20px" width="100%">
          <SingleLineFormItemWrapper>
            <FormItemContainer>
              <Label isRequired>Payment Amount</Label>
              <TextInput
                placeholder="$"
                value={billAmount || ''}
                onChange={handleBillAmountChange}
                error={billAmountError}
                disabled={billAmountDisabled}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>
          <SingleLineFormItemWrapper>
            <FormItemContainer marginRight={'10px'}>
              <Label isRequired>Biller Code</Label>
              <TextInput
                placeholder="BPAY Biller Code"
                value={billerCode || ''}
                onChange={handleBillerCodeChange}
                error={billerCodeError}
                disabled={billAmountDisabled}
              />
            </FormItemContainer>
            <FormItemContainer>
              <Label isRequired>Reference</Label>
              <TextInput
                placeholder='BPAY Reference Number'
                value={billReference || ''}
                onChange={handleBillReferenceChange}
                error={billReferenceError}
                disabled={billAmountDisabled}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>
          {billerLongName && <BillerLongName>{billerLongName}</BillerLongName>}

          <div>
            <Label>Payment Method</Label>
            { !getDefaultPaymentMethod() && <div style={{color: 'rgba(84, 84, 84, 0.56)', marginBottom: '15px', fontSize: '13px'}}>Please select a payment method.</div> }

            <PaymentOptionList>
              <PaymentOption
                onClick={handlePaymentMethodChange('tillPaymentJs')}
                selected={selectedPaymentMethod === 'tillPaymentJs'}
                borderColor={getThemeColor()}
              >
                <PaymentOptionImage src={CreditCardPaymentImage} />
                <PaymentOptionText>Card</PaymentOptionText>
              </PaymentOption>
              { !shouldHideAlipayPlus() &&
                <PaymentOption
                  onClick={handlePaymentMethodChange('alipay')}
                  selected={selectedPaymentMethod === 'alipay'}
                  borderColor={getThemeColor()}
                >
                  <PaymentOptionImage src={AliPayPlusPaymentImage} />
                  <PaymentOptionText>Alipay</PaymentOptionText>
                </PaymentOption>
              }
            </PaymentOptionList>

            { selectedPaymentMethod !== INITIAL_PAYMENT_METHOD && getDisplaySurchargeRate(selectedPaymentMethod) &&
              <SurchargeAmountWrapper>{getPaymentMethodName(selectedPaymentMethod)} Surcharge of {getDisplaySurchargeRate(selectedPaymentMethod)}</SurchargeAmountWrapper>
            }

            <PaymentDetails>
              <PaymentHiddenWrapper show={selectedPaymentMethod === 'tillPaymentJs'}>
                <TillPaymentInlineForm ref={tillPaymentInlineFormRef}
                                       billAmount={billAmount}
                                       billReference={billReference}
                                       userEmail={userEmail}
                                       initLoadingFinishCallback={() => getDefaultPaymentMethod() && setSelectedPaymentMethod(getDefaultPaymentMethod())}
                                       creditCardImage={CreditCardPaymentImageTwo}
                />
              </PaymentHiddenWrapper>
              <PaymentHiddenWrapper show={selectedPaymentMethod === 'alipay'}>
                <PaymentContent>
                  <img src={ContinueToPayImage} />
                  <div>Select ‘Continue to pay’ and you will be redirected to Alipay to complete payment.</div>
                </PaymentContent>
              </PaymentHiddenWrapper>
            </PaymentDetails>

            { paymentResult && <InfoMessage>{paymentResult}</InfoMessage> }

            { shouldVerifyRecaptcha() && <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6LcWNAUrAAAAAHX2haltRqZqMAMZ0qB29zv16EB8"
              size="invisible"
            /> }

            <Agreement>
              <input type="checkbox" id="agreement" />
              <label htmlFor="agreement">
                I have read and agree to the&nbsp;
                <a
                  style={{ textDecoration: 'none' }}
                  href="https://oligo.com.au/legal/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms and Conditions, BPAY Payer Terms, and Privacy Policy
                </a>
                .
              </label>
            </Agreement>

            <Button $themeColor={getThemeColor()} onClick={handlePayBill} block>
              Pay the Bill
            </Button>
          </div>

        </Stack>
      </Stack>
      <Footer />
    </FormContainer>
  );
};
